import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthenticatedLayout from './AuthenticatedLayout';

// Routes that billing_admin is allowed to access
const BILLING_ADMIN_ALLOWED_ROUTES = [
  '/settings',
  '/help',
];

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, organization } = useAuth();
  const location = useLocation();

  // Check if we have auth from navigation state OR from context
  const hasAuthState = location.state?.user;
  const isAuthed = isAuthenticated || hasAuthState || user;
  
  // TRACING
  const traceId = location.state?.traceId || 'no-trace';
  console.log(`[ProtectedRoute][${traceId}] Rendering for path: ${location.pathname}`);
  console.log(`[ProtectedRoute][${traceId}] Auth state:`, {
    isAuthenticated,
    loading,
    hasUser: !!user,
    userId: user?.user_id,
    hasOrg: !!organization,
    hasAuthState: !!hasAuthState,
    isAuthed,
    fromSignup: location.state?.fromSignup
  });

  // Show loading spinner while checking auth (but not if we have state)
  if (loading && !isAuthed) {
    console.log(`[ProtectedRoute][${traceId}] Showing LOADING spinner (loading=${loading}, isAuthed=${isAuthed})`);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login only if definitely not authenticated
  if (!isAuthed && !loading) {
    console.error(`[ProtectedRoute][${traceId}] REDIRECTING TO LOGIN - not authenticated! (isAuthed=${isAuthed}, loading=${loading})`);
    console.error(`[ProtectedRoute][${traceId}] Cookies at redirect:`, document.cookie);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  console.log(`[ProtectedRoute][${traceId}] Auth check PASSED - rendering protected content`);
  
  // Check subscription status - block access if pending payment
  const subscriptionStatus = organization?.subscription_status;
  if (subscriptionStatus === 'pending_payment') {
    console.log(`[ProtectedRoute][${traceId}] Subscription pending_payment - redirecting to billing`);
    // User created account but hasn't paid yet
    // Redirect to settings/billing so they can complete payment
    if (location.pathname !== '/settings') {
      return <Navigate to="/settings?tab=billing&pending=true" replace />;
    }
  }
  
  // Check if owner needs to complete onboarding
  const isOwner = user?.teamRole === 'owner';
  const onboardingCompleted = organization?.onboarding_completed;
  if (isOwner && !onboardingCompleted && location.pathname !== '/onboarding') {
    console.log(`[ProtectedRoute][${traceId}] Owner needs onboarding - redirecting`);
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user is billing_admin and trying to access restricted routes
  const isBillingAdmin = user?.teamRole === 'billing_admin';
  if (isBillingAdmin) {
    const currentPath = location.pathname;
    const isAllowed = BILLING_ADMIN_ALLOWED_ROUTES.some(route => 
      currentPath === route || currentPath.startsWith(route + '/')
    );
    
    // Redirect billing_admin to settings if trying to access restricted routes
    if (!isAllowed) {
      return <Navigate to="/settings?tab=billing" replace />;
    }
  }

  return (
    <AuthenticatedLayout>
      {children}
    </AuthenticatedLayout>
  );
};

export default ProtectedRoute;
